<template>
  <section>
    <div v-if="!hideHeader" class="d-flex flex-row align-items-baseline">
      <slot name="group-header" :group="group">
        <h2 class="font-weight-bold text-dark product-group-name mr-2" v-html="homeSafeFormatting(group.name)"></h2>
      </slot>
      <transition v-if="!alwaysShowAllTags && showControls" appear name="fade">
        <button
          v-if="!showAllTags"
          class="btn product-group-style-toggle --show font-weight-bold text-primary h4"
          @click="showAllTags = true"
        >
          {{ $t('dp.generic.showAll') }}
        </button>
        <button v-else class="btn product-group-style-toggle --hide font-weight-bold text-primary h4" @click="showAllTags = false">
          {{ $t('dp.generic.showLess') }}
        </button>
      </transition>
    </div>
    <div class="d-flex flex-row product-group-style-wrapper align-items-center mw-100">
      <transition name="fade">
        <button
          v-show="!maxScrollLeft && !showAllTags"
          aria-label="Previous Item"
          class="product-group-style-control previous-button --left h-100"
          :aria-controls="encodeURIComponent(group.name.replace(/\s+/g, '-').toLowerCase())"
          @click="prev(windowWidth >= 991 ? 400 : 200)"
        >
          <fa-icon :icon="['fas', 'chevron-left']" size="2x" aria-label="Scroll Left" class="prev" />
        </button>
      </transition>
      <transition :name="showAllTags ? '' : 'fade'">
        <button
          v-show="!showAllTags && showControls && !maxScrollRight"
          aria-label="Next Item"
          class="product-group-style-control next-button --right h-100"
          :aria-controls="encodeURIComponent(group.name.replace(/\s+/g, '-').toLowerCase())"
          @click="next(windowWidth >= 991 ? 400 : 200)"
        >
          <fa-icon :icon="['fas', 'chevron-right']" size="2x" class="next" aria-label="Scroll Right" />
        </button>
      </transition>
      <b-col
        :ref="`stylesSlider${group.id}`"
        cols="auto"
        class="d-flex align-items-stretch product-group-style-row px-0"
        :class="showAllTags ? 'flex-wrap justify-content-around justify-items-center' : 'flex-nowrap'"
        aria-label="Carousel Slides"
        role="region"
      >
        <b-col
          v-for="(style, idx) in group.productStyles"
          :key="style.id"
          :cols="showAllTags ? 12 : 12 / calcColumnsInSlider"
          class="d-flex flex-column product-group-style-col px-0 pr-1"
          md="3-5"
          aria-roledescription="slide"
          role="group"
        >
          <div class="d-flex product-group-style-col-row">
            <span :id="`group-${group.id}-style-desc-${idx}`" class="sr-only">{{
              $t('tags.styleAriaLabel', {
                groupname: group.name,
                idx: idx + 1,
                total: group.productStyles.length,
              })
            }}</span>
            <b-link
              v-testid="{ id: 'tags-style-action', key: style.id }"
              :to="routeToStyle(style)"
              class="d-flex flex-column align-items-center text-wrap text-center pt-2"
              :aria-labelledby="`group-${group.id}-style-desc-${idx} group-${group.id}-style-name-${idx} `"
            >
              <div class="product-group-style-img-wrapper">
                <img
                  v-lazy="{
                    src: style.image && style.image.thumbnail ? getImage(style.image.thumbnail) : null,
                    error: require(`@/assets/images/${$t('tags.placeholderImage')}`),
                    loading: require(`@/assets/images/${$t('tags.placeholderImage')}`),
                  }"
                  alt=""
                  :src="require(`@/assets/images/${$t('tags.placeholderImage')}`)"
                  width="85%"
                  class="img mx-auto"
                />
              </div>
              <div :class="{ 'flex-grow-1': !isClickToActionItem }" class="d-flex flex-column product-group-style-info mt-2">
                <p
                  :id="`group-${group.id}-style-name-${idx}`"
                  :class="!isClickToActionItem ? 'text-dark' : 'text-black'"
                  class="product-group-style-info-name h4"
                  v-html="homeSafeFormatting(style.name)"
                />
              </div>
            </b-link>
            <div class="d-flex flex-column align-items-center text-wrap text-center py-0">
              <div class="product-group-style-img-wrapper">
                <p
                  v-if="style.priceRange && style.priceRange.min !== style.priceRange.max"
                  class="product-group-style-info-price text-dark font-weight-bold pb-2 pt-1 h3"
                >
                  <span>{{ $n(style.priceRange.min, 'currency') }} - {{ $n(style.priceRange.max, 'currency') }} </span>
                </p>
                <p v-else class="product-group-style-info-price text-dark font-weight-bold pb-2 pt-1 h3">
                  <span
                    :class="{ 'text-muted-alt font-weight-normal': style.discountedPrice }"
                    :style="{ textDecoration: style.discountedPrice ? 'line-through' : '' }"
                  >
                    {{ $n(style.price || group.price, 'currency') }}
                  </span>
                  <span v-if="style.discountedPrice" class="ml-1">{{ $n(style.discountedPrice, 'currency') }}</span>
                </p>
              </div>
              <div
                v-if="isClickToActionItem && style.description"
                class="mb-2 d-flex flex-column justify-content-between flex-grow-1 w-100 text-wrap"
              >
                <!-- eslint-disable-next-line vue/no-v-html -->
                <div class="text-left px-3 text-dark style-description" v-html="shortenDescription(style.description)" />
              </div>
            </div>
            <div v-if="isClickToActionItem && actionLinkText">
              <b-link
                :to="routeToStyle(style)"
                :aria-label="`${actionLinkText} ${style.name}`"
                class="product-group-style-action-link btn-primary btn-block text-center"
              >
                {{ actionLinkText }}
              </b-link>
            </div>
          </div>
        </b-col>
      </b-col>
    </div>
  </section>
</template>

<script>
import getImage from '@/mixins/getImage';
import { ProductStylesMixins } from '@/mixins/components/productStyles';
import { homeSafeFormatting } from '@/mixins/homeSafeFormatting';

export default {
  name: 'ProductStyles',
  mixins: [getImage, ProductStylesMixins, homeSafeFormatting],
  props: {
    group: {
      type: Object,
      required: true,
    },
    petId: {
      type: [String, Number],
      default: '',
      required: true,
    },
    alwaysShowAllTags: {
      type: Boolean,
      default: false,
      required: false,
    },
    actionLinkText: {
      type: String,
      default: null,
      required: false,
    },
    hideHeader: {
      type: Boolean,
      default: false,
      required: false,
    },
    isClickToActionItem: {
      type: Boolean,
      default: false,
      required: false,
    },
    redirectPath: {
      type: String,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      showAllTags: this.alwaysShowAllTags,
      maxScrollLeft: true,
      maxScrollRight: false,
      mouseHovering: false,
      windowWidth: window.innerWidth,
    };
  },
  computed: {
    showControls() {
      return this.group?.productStyles?.length > this.calcColumnsInSlider;
    },
    calcColumnsInSlider() {
      if (this.windowWidth > 991) {
        // If window width is greater than 991px, it will show 5 columns
        return 6;
      } else if (this.windowWidth >= 764) {
        // If window width is greater than 764px, it will show 4 columns
        return 3;
      } else {
        // If window width is less than 764px, it will show 2 columns
        return 2;
      }
    },
  },
  updated() {
    if (this.alwaysShowAllTags) {
      this.showAllTags = this.alwaysShowAllTags;
    }
  },
  mounted() {
    window.addEventListener('resize', this.onWindowResize);
    this.$refs[`stylesSlider${this.group?.id}`]?.addEventListener('scroll', this.showHideCarets);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onWindowResize);
  },
  methods: {
    showHideCarets() {
      const stylesSlider = this.$refs[`stylesSlider${this.group?.id}`];
      if (stylesSlider.scrollWidth - stylesSlider.clientWidth === stylesSlider.scrollLeft && stylesSlider.scrollLeft !== 0) {
        this.maxScrollLeft = false;
        this.maxScrollRight = true;
      } else if (stylesSlider.scrollLeft === 0) {
        this.maxScrollLeft = true;
        this.maxScrollRight = false;
      } else if (stylesSlider.scrollWidth - stylesSlider.clientWidth !== 0) {
        this.maxScrollLeft = false;
        this.maxScrollRight = false;
      }
    },
    prev(scrollLeftValue = 200) {
      this.$nextTick(() => {
        const elem = this.$refs[`stylesSlider${this.group?.id}`];
        if (elem.scrollLeft - scrollLeftValue < scrollLeftValue) {
          scrollLeftValue *= 2;
        }
        elem?.scrollBy({
          left: -scrollLeftValue,
          behavior: 'smooth',
        });
        this.showHideCarets();
      });
    },
    next(scrollLeftValue = 200) {
      this.$nextTick(() => {
        const elem = this.$refs[`stylesSlider${this.group?.id}`];
        if (elem.scrollWidth - elem.clientWidth - (elem.scrollLeft + scrollLeftValue) <= scrollLeftValue) {
          scrollLeftValue *= 2;
        }
        elem?.scrollBy({
          left: scrollLeftValue,
          behavior: 'smooth',
        });
        this.showHideCarets();
      });
    },

    onWindowResize() {
      this.windowWidth = window.innerWidth;
      this.$nextTick(() => {
        this.showHideCarets();
      });
    },
    routeToStyle(style) {
      if (!style?.id) {
        return;
      }
      const query = { ...this.$router.currentRoute.query };
      if (this.redirectPath) {
        query.redirect_path = this.redirectPath;
      }
      return !this.petId
        ? { name: 'Style', params: { id: style.id }, query }
        : { name: 'StyleForPet', params: { id: style.id, petId: this.petId }, query };
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@docupet/component-library/src/assets/styles/_variables';
.product-group-style-wrapper {
  position: relative;
  .style-description {
    min-height: 72px;
  }
  .product-group-style-control {
    z-index: 1;
    position: absolute;
    display: inline-flex;
    align-items: center;
    width: 2rem;
    justify-content: center;
    margin-bottom: 0.25rem;
    background-color: transparent;
    &.--left {
      left: 0;
      box-shadow: 10px 0 20px 0 #f8f8f8;
      background-image: linear-gradient(to left, rgba(#f8f8f8, 0.85), rgba(#f8f8f8, 1));
    }
    &.--right {
      left: calc(100% - 2rem);
      color: $dp-navy;
      box-shadow: -10px 0 20px 0 #f8f8f8;
      background-image: linear-gradient(to right, rgba(#f8f8f8, 0.85), rgba(#f8f8f8, 1));
    }
    @media (max-width: 991px) {
      width: 1.5rem;
      &.--right {
        left: calc(100% - 1.5rem);
      }
    }
  }
  .product-group-style-toggle {
    z-index: 1;
    position: absolute;
    display: inline-flex;
    top: calc(100% - 25px);
    left: calc(100% - 200px);
    text-decoration: underline;
    @media (max-width: 764px) {
      left: 70%;
    }
  }
  .product-group-style-row {
    width: 100%;
    overflow-y: hidden;
    white-space: nowrap;
    scroll-snap-type: both proximity;
    transition: all 0.2s ease 0.3s;
    -o-transition: all 0.2s ease 0.3s;
    -moz-transition: all 0.2s ease 0.3s;
    -webkit-transition: all 0.2s ease 0.3s;
    &::after {
      content: '';
      flex: auto;
    }
    &::-webkit-scrollbar {
      height: 0 !important;
    }
    &::-moz-horizontal-scrollbar {
      height: 0 !important;
    }
    &.flex-wrap {
      row-gap: 0.3rem;
      overflow-x: hidden;
      @supports (overflow-x: clip) {
        overflow-x: clip;
      }
      justify-items: center;
      @media (max-width: 991px) {
        display: grid !important;
      }
      @media (max-width: 991px) {
        grid-template-columns: repeat(auto-fill, minmax(90px, 1fr));
      }
      @media (min-width: 764px) and (max-width: 991px) {
        grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
        .col-md-2 {
          max-width: 100%;
        }
      }
      @media (min-width: 476px) and (max-width: 764px) {
        grid-template-columns: repeat(auto-fill, minmax(33.3%, 1fr));
        .col-3 {
          max-width: 100%;
        }
      }
      @media (max-width: 764px) {
        grid-template-columns: repeat(auto-fill, minmax(50%, 1fr));
        .col-3 {
          max-width: 100%;
        }
      }
    }
    .product-group-style-col {
      min-width: 100px;

      &:last-child {
        margin-right: 0;
      }

      @media (min-width: 992px) {
        &.col-md-3-5 {
          flex: 0 0 20%;
          max-width: 20%;
        }
      }

      .product-group-style-col-row {
        cursor: pointer;
        background-color: $dp-gray-1;
        justify-content: flex-start;
        flex-direction: column;
        overflow-x: hidden;
        outline-offset: -1px;
        @supports (overflow-x: clip) {
          overflow-x: clip;
        }
        height: 100%;
        @media (max-width: 991px) {
          img {
            min-height: 75px;
          }
          .product-group-style-img-wrapper {
            min-height: 80px;
          }
        }
        .product-group-style-img-wrapper {
          @media (max-width: 991px) {
            width: 100%;
          }
          @media (min-width: 991px) {
            width: 120%;
          }
        }
        .img {
          object-fit: cover;
          width: 100%;
          width: -moz-available;
          width: -webkit-fill-available;
          width: fill-available;
        }
      }
    }
  }
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}
.text-black {
  color: $dp-black;
}
.previous-button,
.next-button {
  border: none;
}
</style>
